import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import {
  useCreateProductMutation,
  useDeleteProductMutation,
  useLazyFetchProductsQuery,
  useSaveProductOrderMutation,
  useUpdateProductMutation,
} from '../api/eecs-products.api';
import {
  EecsProductsAC,
  clearState,
  setEecsProductModal,
  setIsNeedSaveOrder,
  setSelectedProduct,
} from '../store/eecs-products.slice';
import {
  CreateEecsProductPayload,
  DeleteEecsProductPayload,
  FetchEecsProductsPayload,
  IEecsProduct,
  IEecsProductModal,
  SaveProductOrderPayload,
  UpdateEecsProductPayload,
} from '../types/eecs-products.types';

interface OpenModal {
  modal: IEecsProductModal;
  eecsProduct: IEecsProduct | null;
}

interface UseEecsProducts {
  clearProductsState: () => void;
  openProductsModal: (payload: OpenModal) => void;
  closeProductsModal: () => void;

  fetchProductsHandler: (payload: FetchEecsProductsPayload) => Promise<boolean>;
  deleteProductHandler: (payload: DeleteEecsProductPayload) => Promise<boolean>;
  createProductHandler: (payload: CreateEecsProductPayload) => Promise<IEecsProduct | null>;
  updateProductHandler: (payload: UpdateEecsProductPayload) => Promise<IEecsProduct | null>;
  saveProductOrderHandler: (payload: SaveProductOrderPayload) => Promise<boolean>;
}

export const useEecsProducts = (): UseEecsProducts => {
  const dispatch = useAppDispatch();

  const [fetchProductsQuery] = useLazyFetchProductsQuery();
  const [deleteProductQuery] = useDeleteProductMutation();
  const [createProductQuery] = useCreateProductMutation();
  const [updateProductQuery] = useUpdateProductMutation();
  const [saveProductOrderQuery] = useSaveProductOrderMutation();

  const fetchProductsHandler = async (payload: FetchEecsProductsPayload) => {
    dispatch(EecsProductsAC.toggleLoading(true));
    const { data: response, error } = await fetchProductsQuery(payload);
    dispatch(EecsProductsAC.toggleLoading(false));
    const isError = error || !response?.data;

    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Products!');
    } else {
      batch(() => {
        dispatch(EecsProductsAC.setItems(response.data));
        dispatch(EecsProductsAC.setPaginationMeta(response.meta));
      });
    }

    return !isError;
  };

  const createProductHandler = async (payload: CreateEecsProductPayload) => {
    dispatch(EecsProductsAC.toggleLoading(true));
    const { data: response, error } = await createProductQuery(payload);
    dispatch(EecsProductsAC.toggleLoading(false));
    const isError = error || !response?.data?.id;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to create Product!');
      return null;
    } else {
      toast.success('Product successfully created!');
    }
    dispatch(EecsProductsAC.addItem(response.data));
    dispatch(setIsNeedSaveOrder(true));
    return response.data;
  };

  const updateProductHandler = async (payload: UpdateEecsProductPayload) => {
    dispatch(EecsProductsAC.toggleLoading(true));
    const { data: response, error } = await updateProductQuery(payload);
    dispatch(EecsProductsAC.toggleLoading(false));
    const isError = error || !response?.data?.id;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to update Product!');
      return null;
    } else {
      toast.success('Product successfully updated!');
    }
    dispatch(EecsProductsAC.editItem(response.data));
    return response.data;
  };

  const deleteProductHandler = async (payload: DeleteEecsProductPayload) => {
    dispatch(EecsProductsAC.toggleLoading(true));
    const { error } = await deleteProductQuery(payload);
    dispatch(EecsProductsAC.toggleLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to remove Product!');
    } else {
      toast.success('Product successfully removed!');
      dispatch(EecsProductsAC.removeItem(payload.id));
    }

    return !error;
  };

  const saveProductOrderHandler = async (payload: SaveProductOrderPayload) => {
    dispatch(EecsProductsAC.toggleLoading(true));
    const { error } = await saveProductOrderQuery(payload);
    dispatch(EecsProductsAC.toggleLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to save Order!');
    } else {
      toast.success('Order successfully saved!');
      dispatch(setIsNeedSaveOrder(false));
    }
    return !error;
  };

  const openProductsModal = ({ modal, eecsProduct }: OpenModal) => {
    batch(() => {
      dispatch(setSelectedProduct(eecsProduct));
      dispatch(setEecsProductModal(modal));
    });
  };

  const closeProductsModal = () => {
    batch(() => {
      dispatch(setSelectedProduct(null));
      dispatch(setEecsProductModal(null));
    });
  };

  const clearProductsState = () => {
    dispatch(clearState());
  };

  return {
    fetchProductsHandler,
    deleteProductHandler,
    createProductHandler,
    updateProductHandler,
    saveProductOrderHandler,
    openProductsModal,
    closeProductsModal,
    clearProductsState,
  };
};
